import type { UseTranslationResponse } from "react-i18next";

import {
  BookingConflictErrorType,
  bookingConflictOverlappingTranslationKeysByConflictErrorTypeMap,
  EMPTY_VALUE_DISPLAY,
  formatTimeISO,
  Log,
  type BookingDto,
  type DirectionEnum,
  type ErrorAdditionalT,
  type IApprovalBookingConflict,
  type IRideDto,
  type TimeZoneEnum,
} from "@ryde/shared";

type ResolveOverlappingValueT =
  | { overlappingValue: number | typeof EMPTY_VALUE_DISPLAY }
  | {
      bookingId: number | typeof EMPTY_VALUE_DISPLAY;
      bookingDirection: DirectionEnum | typeof EMPTY_VALUE_DISPLAY;
      bookTime: string | typeof EMPTY_VALUE_DISPLAY;
      originStationName: string | typeof EMPTY_VALUE_DISPLAY;
      destinationStationName: string | typeof EMPTY_VALUE_DISPLAY;
    };

interface IGetOverlappingReasonTranslationKeyByBookingConflictTypeArguments {
  conflictRow: IApprovalBookingConflict;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: UseTranslationResponse<any, any>["t"];
  timeFormat: string;
  timezone: TimeZoneEnum;
}

const resolveOverlappingValues = ({
  errorAdditional,
  errorType,
  timeFormat,
  timezone,
}: {
  errorAdditional: IApprovalBookingConflict["errorAdditional"];
  errorType: IApprovalBookingConflict["errorType"];
  timeFormat: string;
  timezone: TimeZoneEnum;
}): ResolveOverlappingValueT => {
  let errorAdditionalCasted;

  switch (errorType) {
    case BookingConflictErrorType.gapSingleOverlappingBookingBToA:
    case BookingConflictErrorType.gapSingleOverlappingBookingBToB:
    case BookingConflictErrorType.gapSingleOverlappingBookingAToA:
      errorAdditionalCasted = errorAdditional as Record<
        ErrorAdditionalT,
        BookingDto
      >;
      return {
        bookingId: errorAdditionalCasted?.gapSingleOverlappingBooking
          ?.id as number,
        bookingDirection:
          errorAdditionalCasted?.gapSingleOverlappingBooking?.direction ??
          EMPTY_VALUE_DISPLAY,
        bookTime: (
          errorAdditionalCasted?.gapSingleOverlappingBooking as BookingDto
        )?.bookTime
          ? formatTimeISO({
              dateTimeISO: (
                errorAdditionalCasted?.gapSingleOverlappingBooking as BookingDto
              )?.bookTime,
              timeFormat,
              timezone,
            })
          : EMPTY_VALUE_DISPLAY,
        originStationName:
          (errorAdditionalCasted?.gapSingleOverlappingBooking as BookingDto)
            ?.originStation?.name ?? EMPTY_VALUE_DISPLAY,
        destinationStationName:
          (errorAdditionalCasted?.gapSingleOverlappingBooking as BookingDto)
            ?.destinationStation?.name ?? EMPTY_VALUE_DISPLAY,
      };
    case BookingConflictErrorType.overlappingBooking:
      errorAdditionalCasted = errorAdditional as Record<
        ErrorAdditionalT,
        BookingDto
      >;
      return {
        bookingId:
          errorAdditionalCasted?.overlappingBooking?.id ?? EMPTY_VALUE_DISPLAY,
        bookingDirection:
          errorAdditionalCasted?.overlappingBooking?.direction ??
          EMPTY_VALUE_DISPLAY,
        bookTime: (errorAdditionalCasted?.overlappingBooking as BookingDto)
          ?.bookTime
          ? formatTimeISO({
              dateTimeISO: (
                errorAdditionalCasted?.overlappingBooking as BookingDto
              )?.bookTime,
              timeFormat,
              timezone,
            })
          : EMPTY_VALUE_DISPLAY,
        originStationName:
          (errorAdditionalCasted?.overlappingBooking as BookingDto)
            ?.originStation?.name ?? EMPTY_VALUE_DISPLAY,
        destinationStationName:
          (errorAdditionalCasted?.overlappingBooking as BookingDto)
            ?.destinationStation?.name ?? EMPTY_VALUE_DISPLAY,
      };
    case BookingConflictErrorType.existingRide:
      errorAdditionalCasted = errorAdditional as Record<
        ErrorAdditionalT,
        IRideDto
      >;
      return {
        overlappingValue: errorAdditionalCasted?.existingRide?.id as number,
      };
    default:
      Log.error("No additional info for overlapping row conflict");
      return { overlappingValue: EMPTY_VALUE_DISPLAY };
  }
};

const resolveOverlappingTranslationKey = (
  errorType: BookingConflictErrorType,
): string => {
  const key =
    bookingConflictOverlappingTranslationKeysByConflictErrorTypeMap[errorType];

  if (!key) {
    return "overlapping";
  }

  return key;
};

/** The "Overlaps with ..." sentence a rejected row shows, for whichever conflict type produced it. */
export const getOverlappingReasonTranslationKeyByBookingConflictType = ({
  conflictRow,
  t,
  timeFormat,
  timezone,
}: IGetOverlappingReasonTranslationKeyByBookingConflictTypeArguments): string => {
  const overlappingValues = resolveOverlappingValues({
    errorType: conflictRow.errorType,
    errorAdditional: conflictRow.errorAdditional,
    timeFormat,
    timezone,
  });
  const overlappingTranslationKey = resolveOverlappingTranslationKey(
    conflictRow.errorType,
  );

  return t(`booking.bulk.${overlappingTranslationKey}`, overlappingValues);
};
